import { memo } from 'react';

import { CheckboxBase } from '@/lib/base/forms/checkbox';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';

interface CheckboxCellProps {
  isSelected: boolean;
  isDisabled: boolean;
  /**
   * Why the checkbox is disabled, shown as a tooltip. A disabled checkbox
   * with no reason is a dead control the user can only guess at, so callers
   * that disable through projection validity pass the verdict's sentence
   * (`selectionState.getDisabledReason`).
   */
  disabledReason?: string | null;
  onToggle: (actionId: number) => void;
  actionId: number;
  /**
   * The row's position in the rendered list, used for the accessible label.
   * Absent when the row renders outside an `ActionDisplayNumberProvider`
   * (e.g. a table mounted standalone in a test or story), in which case the
   * label drops the number rather than inventing one — same fallback as
   * `ActionCardShell`.
   */
  executionOrder?: number;
  /**
   * Optional tooltip shown on the (enabled) checkbox. Used for `not_visible`
   * verdicts where the action is selectable but some details are hidden.
   */
  tooltip?: string;
}

export const CheckboxCell = memo(function CheckboxCell({
  isSelected,
  isDisabled,
  disabledReason,
  onToggle,
  actionId,
  executionOrder,
  tooltip,
}: CheckboxCellProps) {
  const label =
    executionOrder != null
      ? `Select action ${executionOrder}`
      : 'Select action';

  // A disabled control with no explanation is a dead end, so a disabled row
  // that has a reason keeps its checkbox REACHABLE: `aria-disabled` instead of
  // `disabled`, so it still takes hover and focus and the tooltip can carry
  // the verdict ("Won't apply — Acme Corp won't exist."). The trigger is the
  // control itself — nesting a <button> inside the tooltip's button is
  // invalid HTML. Press handling is a no-op; react-aria's press handling also
  // stops the event reaching the row's onAction, like stopPropagation below.
  if (isDisabled && disabledReason) {
    return (
      <Tooltip title={disabledReason} placement='top'>
        <TooltipTrigger
          aria-label={label}
          aria-disabled='true'
          className='flex items-center'
        >
          <CheckboxBase isSelected={isSelected} isDisabled />
        </TooltipTrigger>
      </Tooltip>
    );
  }

  const button = (
    <button
      type='button'
      onClick={e => {
        e.stopPropagation();
        if (!isDisabled) {
          onToggle(actionId);
        }
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          if (!isDisabled) {
            onToggle(actionId);
          }
        }
      }}
      disabled={isDisabled}
      aria-label={label}
      className='flex items-center'
    >
      <CheckboxBase isSelected={isSelected} isDisabled={isDisabled} />
    </button>
  );

  if (tooltip) {
    // The control IS the trigger — a TooltipTrigger (react-aria Button). Wrapping
    // the native `button` above in a TooltipTrigger would nest a <button> inside a
    // <button> (invalid HTML: the parser closes the outer one, so the tooltip
    // never shows). This mirrors the disabled+disabledReason branch above.
    // react-aria's press handling also stops the event reaching the row's
    // onAction, like the stopPropagation on the native button.
    return (
      <Tooltip title={tooltip} placement='top'>
        <TooltipTrigger
          aria-label={label}
          className='flex items-center'
          isDisabled={isDisabled}
          onPress={() => onToggle(actionId)}
        >
          <CheckboxBase isSelected={isSelected} isDisabled={isDisabled} />
        </TooltipTrigger>
      </Tooltip>
    );
  }

  return button;
});
